document.documentElement.classList.remove('no-js');

function secureExternalLinks(root = document) {
  root.querySelectorAll('a[href]').forEach((link) => {
    let destination;
    try {
      destination = new URL(link.getAttribute('href'), window.location.href);
    } catch (_error) {
      return;
    }
    if (!['http:', 'https:'].includes(destination.protocol) || destination.origin === window.location.origin) return;
    const relation = new Set((link.getAttribute('rel') || '').split(/\s+/).filter(Boolean));
    relation.add('nofollow');
    relation.add('external');
    if (link.target === '_blank') relation.add('noopener');
    link.setAttribute('rel', [...relation].join(' '));
  });
}

secureExternalLinks();

class PLCHeader {
  constructor(root) {
    this.root = root;
    this.openButton = root.querySelector('[data-menu-toggle]');
    this.closeButton = root.querySelector('[data-menu-close]');
    this.menu = root.querySelector('[data-mobile-menu]');
    this.lastFocused = null;
    this.openButton?.addEventListener('click', () => this.open());
    this.closeButton?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (event) => {
      if (this.menu?.hidden) return;
      if (event.key === 'Escape') this.close();
      if (event.key === 'Tab') this.trapFocus(event);
    });
  }

  open() {
    if (!this.menu) return;
    this.lastFocused = document.activeElement;
    this.menu.hidden = false;
    document.body.classList.add('menu-open');
    this.openButton?.setAttribute('aria-expanded', 'true');
    this.closeButton?.focus();
  }

  close() {
    if (!this.menu) return;
    this.menu.hidden = true;
    document.body.classList.remove('menu-open');
    this.openButton?.setAttribute('aria-expanded', 'false');
    this.lastFocused?.focus();
  }

  trapFocus(event) {
    const focusable = [...this.menu.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')];
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

class PredictiveModelSearch {
  constructor(form) {
    this.form = form;
    this.input = form.querySelector('[data-predictive-search-input]');
    this.results = form.querySelector('[data-predictive-search-results]');
    this.controller = null;
    this.timer = null;
    this.activeOptionIndex = -1;
    this.input?.addEventListener('input', () => this.schedule());
    this.input?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') return this.close();
      if (event.key === 'ArrowDown') return this.moveActiveOption(event, 1);
      if (event.key === 'ArrowUp') return this.moveActiveOption(event, -1);
      if (event.key === 'Enter' && this.activeOptionIndex >= 0) {
        const option = this.options[this.activeOptionIndex];
        if (option?.href) {
          event.preventDefault();
          window.location.assign(option.href);
        }
      }
    });
    document.addEventListener('click', (event) => {
      if (!form.contains(event.target)) this.close();
    });
  }

  schedule() {
    window.clearTimeout(this.timer);
    const query = this.input.value.trim();
    if (query.length < 2) return this.close();
    this.timer = window.setTimeout(() => this.fetch(query), 180);
  }

  async fetch(query) {
    this.controller?.abort();
    this.controller = new AbortController();
    const url = `/search/suggest?q=${encodeURIComponent(query)}&resources[type]=product,collection,article,page&resources[limit]=6&section_id=predictive-search`;
    try {
      const response = await window.fetch(url, { signal: this.controller.signal });
      if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
      const markup = await response.text();
      const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
      const section = documentFragment.querySelector('#shopify-section-predictive-search');
      this.results.innerHTML = section?.innerHTML || '';
      secureExternalLinks(this.results);
      this.options.forEach((option, index) => {
        option.id = `${this.results.id}-Option-${index}`;
        option.setAttribute('aria-selected', 'false');
      });
      this.activeOptionIndex = -1;
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    } catch (error) {
      if (error.name !== 'AbortError') this.close();
    }
  }

  close() {
    if (!this.results || !this.input) return;
    this.results.hidden = true;
    this.results.innerHTML = '';
    this.activeOptionIndex = -1;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
  }

  get options() {
    return [...this.results.querySelectorAll('[role="option"]')];
  }

  moveActiveOption(event, direction) {
    const options = this.options;
    if (this.results.hidden || options.length === 0) return;
    event.preventDefault();
    this.activeOptionIndex = (this.activeOptionIndex + direction + options.length) % options.length;
    options.forEach((option, index) => option.setAttribute('aria-selected', index === this.activeOptionIndex ? 'true' : 'false'));
    const activeOption = options[this.activeOptionIndex];
    this.input.setAttribute('aria-activedescendant', activeOption.id);
    activeOption.scrollIntoView({ block: 'nearest' });
  }
}

document.querySelectorAll('.site-header').forEach((header) => new PLCHeader(header));
document.querySelectorAll('[data-predictive-search-form]').forEach((form) => new PredictiveModelSearch(form));
